import React from 'react';
import { useMutation } from 'urql';
import { Interpolation } from '@emotion/react';

import {
  Stack,
  Button,
  Text,
  Heading,
  ShoppingListPod,
  EditableItemPod,
  DetailDialogPod,
  merge
} from '@quotient/riq-react-ui';
import { queryStringify } from '@quotient/riq-helpers';

import { addItemMutation, updateItemMutation, deleteItemMutation } from './queries';
import { useItemSeperator, generateNewItem } from './helpers';

import { SLEmailDialog } from './SLEmailDialog';
import { LoginDismissal } from './LoginDismissal';
import { ShoppingListContext } from './state';
import { CartCounter } from './CartCounter';
import { RenderSortFilters } from './SortFilters';
import { AddItemForm, IAddItemFormProps } from './AddItemForm';

interface SLCartCounterProps {
  colors?: IStateColors;
  onClick?: () => void;
}
interface SLHeaderProps {
  title?: string;
}
interface SLBodyProps {
  closeIcon?: JSX.Element;
  css?: Interpolation<any>;
  slButtonVariant?: string;
  data: any;
}
interface SLFooterProps {
  colors?: IStateColors;
  closeIcon?: JSX.Element;
  dialogButtonVariant?: string;
}

interface ShoppingListComponents {
  CartCounter: React.FC<SLCartCounterProps>;
  Header: React.FC<SLHeaderProps>;
  Body: React.FC<SLBodyProps>;
  Footer: React.FC<SLFooterProps>;
  AddItemForm: React.FC<IAddItemFormProps>;
}

interface ShoppingListProps {
  data: any;
}

const ShoppingList: React.FC<ShoppingListProps> & ShoppingListComponents = ({ data, children }) => {
  const { dispatch } = React.useContext(ShoppingListContext);

  React.useEffect(() => {
    dispatch({ type: 'SET_INIT_STATE', payload: data });
  }, [data]);

  return <div>{children}</div>;
};

const CartCounterComp = (props) => {
  const { state } = React.useContext(ShoppingListContext);
  const count = state?.arrItems?.length;
  return <CartCounter count={count} {...props} />;
};
ShoppingList.CartCounter = CartCounterComp;

const Header = ({ title = 'My Shopping List' }) => {
  return (
    <Stack css={{ paddingX: 5, paddingTop: 5, background: 'grays.200' }}>
      <Heading as="h2" size="section" css={{ fontWeight: 'normal' }}>
        {title}
      </Heading>
    </Stack>
  );
};

ShoppingList.Header = Header;

const AddItemFormComp = (props) => {
  const { state, dispatch } = React.useContext(ShoppingListContext);
  const [, addShoppingListItem] = useMutation(addItemMutation);

  const addItemRequest = ({ listId, itemDetails }: any) => {
    const variables = { listId, itemDetails };
    return addShoppingListItem(variables)
      .then((result) => {
        console.log(result);
        if (listId === '') {
          dispatch({ type: 'SET_LIST_ID', payload: result.data?.addShoppingListItem?.listId });
        }
        if (result.data?.addShoppingListItem?.itemDetails?.itemId) {
          return result.data.addShoppingListItem?.itemDetails;
        }
      })
      .catch((errors) => {
        console.log(errors);
        return;
      });
  };

  const addItem = ({ itemName }) => {
    const item = { listId: state.listId, itemDetails: generateNewItem(itemName) };
    addItemRequest(item).then((updatedItem) => {
      updatedItem && dispatch({ type: 'ADD_ITEM', payload: updatedItem });
    });
  };

  return <AddItemForm onSubmit={addItem} {...props} />;
};

ShoppingList.AddItemForm = AddItemFormComp;

const Body = ({ children, closeIcon, css, slButtonVariant = 'primary', data }) => {
  const { state, dispatch } = React.useContext(ShoppingListContext);
  const subtitle = state.showListIdentifier ? state.listIdentifier : '';

  const [SLItemData, setSLItemData] = React.useState(null);
  const [showSLItemModal, setShowSLItemModal] = React.useState(false);

  const openSLItemModal = () => setShowSLItemModal(true);
  const closeSLItemModal = () => setShowSLItemModal(false);

  const [crossoutItems, nonCrossOutItems, tempCrossoutItems] = useItemSeperator(state.arrItems, state.currentFilter);
  const [, updateShoppingListItem] = useMutation(updateItemMutation);
  const [, deleteShoppingListItem] = useMutation(deleteItemMutation);

  const updateItemRequest = ({ listId, itemDetails }: any) => {
    const variables = { listId, itemDetails };
    return updateShoppingListItem(variables)
      .then((result) => {
        console.log(result);
        console.log(result.data?.updateShoppingListItem?.itemDetails?.itemId);
        if (result.data?.updateShoppingListItem?.itemDetails?.itemId) {
          return result.data.updateShoppingListItem?.itemDetails;
        }
      })
      .catch((errors) => {
        console.log(errors);
        return;
      });
  };

  const editItem = (item) => {
    const oldCrossOutState = item?.customAttributes?.oldCrossOut;
    const keysToRemove = ['__typename', 'oldCrossOut', 'tempCrossout'];
    item = JSON.parse(JSON.stringify(item), (key: string, value: any) =>
      keysToRemove.indexOf(key) > -1 ? undefined : value
    );

    let variables = { listId: state.listId, itemDetails: item };

    updateItemRequest(variables).then((updatedItem) => {
      const { customAttributes, ...restUpdatedItems } = updatedItem;
      const { crossout, ...restCustomAttributes } = customAttributes;

      updatedItem &&
        dispatch({
          type: 'UPDATE_ITEM',
          payload: {
            ...restUpdatedItems,
            customAttributes: { ...restCustomAttributes, crossout: oldCrossOutState, tempCrossout: crossout }
          }
        });
    });
  };

  const deleteItemRequest = ({ listId, itemIds }: any) => {
    const variables = { listId, itemIds };
    return deleteShoppingListItem(variables)
      .then((result) => {
        console.log(result);
        if (result.data?.deleteShoppingListItem?.status === 'SUCCESS') {
          return result.data.deleteShoppingListItem;
        }
      })
      .catch((errors) => {
        console.log(errors);
        return;
      });
  };

  const deleteItem = (itemIds: Array<string>) => () => {
    if (!Array.isArray(itemIds)) {
      itemIds = [itemIds];
    }
    deleteItemRequest({ listId: state.listId, itemIds }).then((res) => {
      if (res.status === 'SUCCESS') {
        dispatch({ type: 'REMOVE_ITEM', payload: { listId: state.listId, itemIds } });
      }
    });
  };

  // delete shoping list items
  const removeAllCheckedItems = () => {
    const itemIds = [...crossoutItems.map((a) => a.itemId), ...tempCrossoutItems.map((a) => a.itemId)];
    itemIds && deleteItem(itemIds)();
  };

  const renderPods = (items) => {
    if (!items || !Array.isArray(items)) return null;

    return items.map((item) => {
      if (!item) return null;

      if (item.itemName && item.customAttributes?.source === 'ITEM')
        return <EditableItemPod key={item.itemId} data={item} onUpdate={editItem} onDelete={deleteItem} />;

      return (
        item.offer && (
          <ShoppingListPod
            onClick={() => {
              setSLItemData(item);
              openSLItemModal();
            }}
            key={item.itemId}
            data={item?.offer}
          />
        )
      );
    });
  };

  const setCurrentFilter = (filter) => dispatch({ type: 'SET_FILTER', payload: filter });

  return (
    <Stack direction="vertical" css={{ display: 'block', height: '90vh', paddingTop: 0, overflow: 'auto' }}>
      <Text size={4} align="left" css={{ display: 'block', background: 'grays.200', paddingX: 5 }}>
        {subtitle}
      </Text>

      <RenderSortFilters
        currentFilter={state.currentFilter}
        sortFilters={state.sortfilter_data.sortFilters}
        filterHeading={state.sortfilter_data.headingText}
        setCurrentFilter={setCurrentFilter}
      />

      {children}

      <LoginDismissal show={!state.isUserLoggedIn} url={state.signinlink} />

      <Stack direction="vertical" css={{ padding: 5 }}>
        {renderPods(nonCrossOutItems)}
        {renderPods(crossoutItems)}

        <Button
          variant={slButtonVariant}
          disabled={!tempCrossoutItems.length}
          size="large"
          marginX="auto"
          marginY="8"
          css={{
            width: '240px',
            display:
              (data?.arrItems?.length > 0 && state.isUserLoggedIn) || crossoutItems.length || nonCrossOutItems.length
                ? 'inline-flex'
                : 'none',
            borderRadius: '0',
            pointerEvents: tempCrossoutItems.length ? 'auto' : 'none'
          }}
          onClick={removeAllCheckedItems}
        >
          Remove All Checked Items
        </Button>

        {!state.arrItems?.length && (
          <>
            <Text weight="bold">
              You don&apos;t have any items in your list.
              <Text weight="normal"> Start making your shopping list and take it everywhere you go.</Text>
            </Text>
          </>
        )}
      </Stack>

      <DetailDialogPod
        isOpen={showSLItemModal}
        closeFn={closeSLItemModal}
        data={SLItemData}
        closeIcon={closeIcon}
        activeButtonCss={css}
      />
    </Stack>
  );
};

ShoppingList.Body = Body;

interface IStateColors {
  bgActive?: string;
  bgInactive?: string;
  textColorActive?: string;
  textColorInActive?: string;
}

const defaultStateColors: IStateColors = {
  bgActive: 'primary',
  bgInactive: 'grays.400',
  textColorActive: 'primary',
  textColorInActive: 'primary'
};

const Footer = ({ colors, closeIcon, dialogButtonVariant }) => {
  const { state } = React.useContext(ShoppingListContext);
  const stateColors: IStateColors = merge(defaultStateColors, colors);
  // Open and closing email dialog/ modal window
  const [showEmailModal, setShowEmailModal] = React.useState(false);
  const openEmailModal = () => setShowEmailModal(true);
  const closeEmailModal = () => setShowEmailModal(false);

  // print releated fxns
  const getCurrentSort = () => {
    return queryStringify({ sb: '', so: '', filters: state.currentFilter.join('~') });
  };

  const handlePrintList = () => {
    if (!state.arrItems.length) return;
    const popupURL = '/print/shoppingList/?' + getCurrentSort();

    if (typeof window !== 'undefined')
      window.open(popupURL, 'myshoppinglist', 'width=500, height=500, resizable=yes, scrollbars=yes');
  };

  const handlePrintListonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.keyCode === 13) handlePrintList();
  };

  return (
    <>
      <Stack gap={'1px'} css={{ position: 'absolute', bottom: 0, width: '100%' }}>
        <Button
          css={{
            borderRadius: 0,
            backgroundColor: state.arrItems?.length ? stateColors.bgActive : stateColors.bgInactive,
            color: state.arrItems?.length ? stateColors.textColorActive : stateColors.textColorInActive
          }}
          onKeyDown={handlePrintListonKeyDown}
          onClick={handlePrintList}
          disabled={!state.arrItems.length}
          fullWidth
        >
          print list
        </Button>
        <Button
          css={{
            borderRadius: 0,
            backgroundColor: state.arrItems?.length ? stateColors.bgActive : stateColors.bgInactive,
            color: state.arrItems?.length ? stateColors.textColorActive : stateColors.textColorInActive
          }}
          onClick={openEmailModal}
          disabled={!state.arrItems.length}
          fullWidth
        >
          email list
        </Button>
      </Stack>
      <SLEmailDialog
        isOpen={showEmailModal}
        closeFn={closeEmailModal}
        data={{ listId: '', listIdentifier: '', crossoutItems: [], nonCrossOutItems: [] }}
        closeIcon={closeIcon}
        buttonVariant={dialogButtonVariant}
      />
    </>
  );
};

ShoppingList.Footer = Footer;

export { ShoppingList };
